import { formatCurrency, formatDate, formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { getOwnershipLabel } from "@/shared/constants/ownershipTypes";
import { getEstimateRangeLabel, isEstimatedItem } from "@/shared/utils/balanceEstimates";
import { buildAddress, buildAddressFromMetadata } from "@/shared/utils/addressUtils";
import type { AddressParts } from "@/shared/utils/addressUtils";

import type { EstateItem } from "@/shared/types/estateTypes";

export type DetailFieldType =
  | "text"
  | "value"
  | "ownership"
  | "identifier"
  | "address"
  | "propertyAddress";

export interface DetailField {
  label: string;
  value: string;
  fieldType?: DetailFieldType;
}

export interface CategoryDetails {
  sectionTitle: string;
  valueLabel: string;
  subtitle: string;
  fields: DetailField[];
}

function getMetaString(item: EstateItem, key: string): string | null {
  const val = item.metadata?.[key];
  return typeof val === "string" && val ? val : null;
}

function buildSubtitle(parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(" \u00b7 ");
}

function currencyField(label: string, raw: string | null | undefined): DetailField | null {
  if (!raw) return null;
  const num = Number(raw);
  return Number.isFinite(num) ? { label, value: formatCurrency(num) } : null;
}

function getValueDate(item: EstateItem): string | null {
  if (item.last_updated_date) return formatPersonDateForDisplay(item.last_updated_date);
  if (item.plaid_last_refreshed_at) return formatDate(item.plaid_last_refreshed_at);
  return null;
}

function valueDateLabel(label: string, item: EstateItem): string {
  const date = getValueDate(item);
  return date ? `${label} as of ${date}` : label;
}

function displayValue(item: EstateItem): string {
  const rangeLabel = isEstimatedItem(item) ? getEstimateRangeLabel(item) : null;
  return rangeLabel ? `~${rangeLabel}` : formatCurrency(item.balance);
}

function propertyAddress(item: EstateItem, canonical: string | null | undefined): string | null {
  if (canonical) return canonical;
  const stored = item.identifiers?.address;
  if (typeof stored === "string" && stored.trim()) return stored.trim();
  return buildAddressFromMetadata(item.metadata).trim() || null;
}

export function getCategoryDetails(
  item: EstateItem,
  canonicalAddress?: string | null,
): CategoryDetails {
  const category = item.category;
  const institution = item.identifiers?.institution as string | undefined;
  const last4 = item.plaid_account_mask || (item.identifiers?.account_last4 as string | undefined);
  const masked = last4 ? `\u00b7\u00b7\u00b7\u00b7 ${last4}` : null;

  const f = (
    label: string,
    value: string | null | undefined,
    fieldType?: DetailFieldType,
  ): DetailField | null => (value ? { label, value, fieldType } : null);
  const compact = (arr: (DetailField | null)[]): DetailField[] =>
    arr.filter((x): x is DetailField => x !== null);

  const m = (key: string) => getMetaString(item, key);
  const ownership = getOwnershipLabel(item.ownership) ?? m("ownership_type");
  const coOwners = m("co_owners");
  const trustName = m("trust_name");
  const ownershipFields = [
    {
      label: "Ownership type",
      value: ownership || "Not set",
      fieldType: "ownership" as DetailFieldType,
    },
    f("Co-owner name(s)", coOwners),
    f("Ownership share (%)", m("ownership_share")),
    f("Trust name", trustName),
  ];

  if (category === "Properties") {
    const city = m("city");
    const state = m("state");
    return {
      sectionTitle: "Property details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type, [city, state].filter(Boolean).join(", ")]),
      fields: compact([
        {
          label: "Address",
          value: propertyAddress(item, canonicalAddress) ?? "Not added",
          fieldType: "propertyAddress" as DetailFieldType,
        },
        f("Property type", item.item_type),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
        ...ownershipFields,
      ]),
    };
  }

  if (category === "Vehicles") {
    const year = item.metadata?.year ? String(item.metadata.year) : null;
    return {
      sectionTitle: "Vehicle details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type, [year, m("model")].filter(Boolean).join(" ")]),
      fields: compact([
        f("Make", m("make")),
        f("Model", m("model")),
        f("Year", year),
        f("VIN", m("vin")),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
        ...ownershipFields,
      ]),
    };
  }

  if (category === "Life insurance") {
    return {
      sectionTitle: "Policy details",
      valueLabel: "Death benefit",
      subtitle: institution || item.category,
      fields: compact([
        f("Last 4 of policy number", masked, "identifier"),
        f("Insured", m("insured")),
        f("Policy owner", m("owner_of_policy")),
        f("Trust name", trustName),
        f("In force", m("in_force")),
        f(valueDateLabel("Death benefit", item), displayValue(item), "value"),
        currencyField("Cash value", m("cash_value")),
        currencyField("Cash surrender value", m("cash_surrender_value")),
        currencyField("Outstanding policy loan", m("outstanding_policy_loan")),
        f("Term length", m("term_length")),
        f("Term end date", m("term_end_date")),
        f("Claim status", m("claim_status")),
      ]),
    };
  }

  if (category === "Long-term care insurance") {
    return {
      sectionTitle: "Policy details",
      valueLabel: "Benefit amount",
      subtitle: institution || item.category,
      fields: compact([
        f("Last 4 of policy number", masked, "identifier"),
        f("Insured", m("insured")),
        f("Policy owner", m("owner_of_policy")),
        f("Trust name", trustName),
        f("Daily benefit amount", m("daily_benefit_amount")),
        f("Benefit period remaining", m("benefit_period_remaining")),
        f("In force", m("in_force")),
        f("Claim status", m("claim_status")),
      ]),
    };
  }

  if (category === "Annuity contracts") {
    return {
      sectionTitle: "Contract details",
      valueLabel: "Fair market value",
      subtitle: institution || item.category,
      fields: compact([
        f("Last 4 of policy number", masked, "identifier"),
        f("Annuitant", m("annuitant")),
        f("Policy owner", m("owner_of_policy")),
        f("Trust name", trustName),
        f("Annuity phase", m("annuity_phase")),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
        currencyField("Death benefit", m("death_benefit")),
      ]),
    };
  }

  if (category === "Valuables") {
    return {
      sectionTitle: "Item details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type]),
      fields: compact([
        f("Description", m("description")),
        f("Artist or maker", m("artist_or_maker")),
        f("Maker or brand", m("maker_or_brand")),
        f("Title of work", m("title_of_work")),
        f("Metal type", m("metal_type")),
        f("Form", m("form")),
        f("Quantity", m("quantity")),
        f("Weight per item", m("weight_per_item")),
        f("Storage location", m("storage_location")),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
        ...ownershipFields,
      ]),
    };
  }

  if (category === "Safe or safety deposit box") {
    const addressParts = item.metadata?.address as AddressParts | undefined;
    const addressStr = addressParts ? buildAddress(addressParts).trim() : "";
    const boxNumber = m("box_number");
    return {
      sectionTitle: "Safe details",
      valueLabel: "Fair market value",
      subtitle: item.item_type || item.category,
      fields: compact([
        { label: "Address", value: addressStr || "Not added", fieldType: "address" },
        f("Box number", boxNumber),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
        ...ownershipFields,
      ]),
    };
  }

  if (category === "LLC or partnership interest") {
    return {
      sectionTitle: "Business details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type]),
      fields: compact([
        f("Stake in entity (%)", m("ownership_share")),
        f("Other owners", m("partners")),
        ...ownershipFields,
      ]),
    };
  }

  if (category === "Stock in a private company") {
    return {
      sectionTitle: "Business details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type]),
      fields: compact([
        f("Stake in entity (%)", m("ownership_share")),
        f("Number of shares / units", m("number_of_shares")),
        ...ownershipFields,
      ]),
    };
  }

  if (category === "Sole proprietorship") {
    return {
      sectionTitle: "Business details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type]),
      fields: compact([f("Plans for asset", m("plans_for_asset"))]),
    };
  }

  if (category === "Receivables") {
    return {
      sectionTitle: "Receivable details",
      valueLabel: "Fair market value",
      subtitle: institution || item.category,
      fields: compact([
        f("Description", m("description")),
        f("Last 4 of policy number", masked, "identifier"),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
        f("Claim status", m("claim_status")),
        f("Interest rate", m("interest_rate")),
        f("Maturity date", m("maturity_date")),
      ]),
    };
  }

  if (category === "Work-related pay and shares") {
    return {
      sectionTitle: "Compensation details",
      valueLabel: "Fair market value",
      subtitle: institution || item.category,
      fields: compact([
        f("Number of shares / units", m("number_of_shares")),
        f("Vested portion", m("vested_portion")),
        f("Strike price", m("strike_price")),
        f("Expiration date", m("expiration_date")),
        f("Vesting schedule", m("vesting_schedule")),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
      ]),
    };
  }

  if (category === "Royalties and Intellectual Property") {
    return {
      sectionTitle: "IP details",
      valueLabel: "Fair market value",
      subtitle: buildSubtitle([item.item_type]),
      fields: compact([
        f("Description", m("description")),
        f("Patent number", m("patent_number")),
        f("Expiration date", m("expiration_date")),
        f(valueDateLabel("Fair market value", item), displayValue(item), "value"),
      ]),
    };
  }

  if (category === "Prepaid Services and Credits") {
    return {
      sectionTitle: "Details",
      valueLabel: "Fair market value",
      subtitle: institution || item.category,
      fields: compact([f(valueDateLabel("Fair market value", item), displayValue(item), "value")]),
    };
  }

  if (category === "Unsecured Debts") {
    return {
      sectionTitle: "Debt details",
      valueLabel: "Principal balance owed",
      subtitle: institution || item.category,
      fields: compact([
        f("Last 4 of account number", masked, "identifier"),
        f(valueDateLabel("Principal balance owed", item), displayValue(item), "value"),
        f("Dispute status", m("dispute_status")),
      ]),
    };
  }

  if (category === "Secured debts") {
    return {
      sectionTitle: "Loan details",
      valueLabel: "Principal balance owed",
      subtitle: institution || item.category,
      fields: compact([
        f("Last 4 of account number", masked, "identifier"),
        currencyField("Monthly payment", m("monthly_payment")),
        f("Interest rate", m("interest_rate")),
        f("Maturity date", m("maturity_date")),
        f(valueDateLabel("Principal balance owed", item), displayValue(item), "value"),
      ]),
    };
  }

  if (
    category === "Ongoing Estate Expenses" &&
    item.item_type === "Professional and service fees"
  ) {
    return {
      sectionTitle: "Payment details",
      valueLabel: "Amount paid",
      subtitle: m("payee") || item.category,
      fields: compact([
        f("Paid to", m("payee")),
        f("Amount paid", formatCurrency(item.balance), "value"),
        f("Paid on", formatPersonDateForDisplay(m("paid_on"), undefined, "")),
      ]),
    };
  }

  if (category === "Ongoing Estate Expenses") {
    return {
      sectionTitle: "Expense details",
      valueLabel: "Balance owed",
      subtitle: institution || item.category,
      fields: compact([
        currencyField("Recurring amount", m("recurring_amount")),
        f(valueDateLabel("Balance owed", item), displayValue(item), "value"),
        f("Activation status", m("activation_status")),
      ]),
    };
  }

  if (category === "Contingent Liabilities") {
    return {
      sectionTitle: "Liability details",
      valueLabel: "Estimated exposure",
      subtitle: institution || item.category,
      fields: compact([
        f(valueDateLabel("Estimated exposure", item), displayValue(item), "value"),
        f("Primary borrower", m("primary_borrower")),
      ]),
    };
  }

  if (category === "Tax Liabilities") {
    return {
      sectionTitle: "Tax details",
      valueLabel: "Estimated balance",
      subtitle: institution || item.category,
      fields: compact([
        f(valueDateLabel("Estimated balance", item), displayValue(item), "value"),
        currencyField("Monthly payment", m("monthly_payment")),
      ]),
    };
  }

  const vlabel =
    category === "Retirement accounts"
      ? item.value_label || "Vested balance"
      : item.value_label || "Account balance";

  return {
    sectionTitle: "Account details",
    valueLabel: vlabel,
    subtitle: institution ? (item.item_type ?? item.category) : item.category,
    fields: compact([
      f("Last 4 of account number", masked, "identifier"),
      f(valueDateLabel(vlabel, item), displayValue(item), "value"),
      ...ownershipFields,
    ]),
  };
}
